import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ClipboardCheck, FileDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const quando = (iso) => (iso ? new Date(iso).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : '');

/**
 * Registro, per nominativo, di chi ha preso visione dei verbali di assemblea.
 * Si compila da solo quando un condomino preme il pulsante di attestazione.
 * Per il verbale scelto mostra anche chi non ha ancora confermato.
 */
export default function RegistroPresaVisione({ open, onOpenChange, condominiumId }) {
  const { token } = useAuth();
  const [dati, setDati] = useState(null);
  const [verbale, setVerbale] = useState('TUTTI');
  const [caricamento, setCaricamento] = useState(false);

  const carica = useCallback(async () => {
    if (!open || !condominiumId || !token) return;
    setCaricamento(true);
    try {
      const filtro = verbale !== 'TUTTI' ? `?document_id=${encodeURIComponent(verbale)}` : '';
      const r = await fetch(`${API_URL}/api/condominiums/${condominiumId}/presa-visione/registro${filtro}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) { toast.error('Registro non disponibile'); return; }
      setDati(await r.json());
    } catch (e) {
      toast.error('Errore di connessione');
    } finally {
      setCaricamento(false);
    }
  }, [open, condominiumId, token, verbale]);

  useEffect(() => { carica(); }, [carica]);

  const voci = useMemo(() => dati?.voci || [], [dati]);

  const esportaCsv = () => {
    const cella = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const righe = [['Verbale', 'Nominativo', 'Unità', 'Email', 'Presa visione il', 'IP'].map(cella).join(';')];
    voci.forEach((v) => righe.push([v.document_title, v.nome, v.unita, v.email, quando(v.presa_visione_il), v.ip].map(cella).join(';')));
    const blob = new Blob(['﻿' + righe.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'registro-presa-visione-verbali.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-[96vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ClipboardCheck className="h-5 w-5 text-green-700" /> Registro presa visione dei verbali</DialogTitle>
          <DialogDescription>
            Si aggiorna da solo: ogni volta che un condomino preme «Attesto di aver preso visione» si aggiunge una riga con nome, unità, data e ora.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          <Select value={verbale} onValueChange={setVerbale}>
            <SelectTrigger className="w-full sm:w-96"><SelectValue placeholder="Verbale" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="TUTTI">Tutti i verbali</SelectItem>
              {(dati?.verbali || []).map((v) => <SelectItem key={v.id} value={v.id}>{v.title}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={esportaCsv} disabled={!voci.length}><FileDown className="mr-2 h-4 w-4" />Esporta CSV</Button>
          {dati?.totale_condomini != null && (
            <span className="text-sm text-gray-600">{voci.length} su {dati.totale_condomini} condomini hanno confermato</span>
          )}
        </div>

        <Table>
          <TableHeader>
            <TableRow><TableHead>Nominativo</TableHead><TableHead>Unità</TableHead>{verbale === 'TUTTI' && <TableHead>Verbale</TableHead>}<TableHead>Presa visione il</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {voci.map((v) => (
              <TableRow key={v.id}>
                <TableCell>{v.nome}<div className="text-xs text-gray-500">{v.email}</div></TableCell>
                <TableCell>{v.unita}</TableCell>
                {verbale === 'TUTTI' && <TableCell className="text-sm">{v.document_title}</TableCell>}
                <TableCell>{quando(v.presa_visione_il)}</TableCell>
              </TableRow>
            ))}
            {!voci.length && !caricamento && (
              <TableRow><TableCell colSpan={4} className="text-center text-sm text-gray-500">Nessuna presa visione registrata.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>

        {dati?.mancanti && (
          <div className="mt-2">
            <h4 className="text-sm font-semibold mb-1">Non hanno ancora confermato ({dati.mancanti.length})</h4>
            <p className="text-sm text-gray-700">{dati.mancanti.map((m) => m.nome || m.email).join(' · ') || 'Hanno confermato tutti.'}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
